import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { CsvExportButton } from '../../components/data/CsvExportButton.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency } from '../../utils/formatters.js';

import { AssetCreateModal } from './AssetCreateModal.jsx';
import { DisposeAssetModal } from './DisposeAssetModal.jsx';

const CSV_COLUMNS = [
  { key: 'assetNumber', label: 'Asset' },
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Category' },
  { key: 'acquisitionCost', label: 'Cost' },
  { key: 'accumulatedDepreciation', label: 'AccumulatedDepreciation' },
  { key: 'netBookValue', label: 'NetBookValue' },
  { key: 'status', label: 'Status' },
];

const STATUS_VARIANT = { ACTIVE: 'success', FULLY_DEPRECIATED: 'warning', DISPOSED: 'neutral' };

function currentMonth() {
  // type="month" wants YYYY-MM; build it without Date() locale surprises.
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function FixedAssetsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [disposing, setDisposing] = useState(null);
  const [period, setPeriod] = useState(currentMonth());
  const [runMsg, setRunMsg] = useState(null);

  const insights = useQuery({
    queryKey: ['finance', 'fixed-asset-insights'],
    queryFn: () => financeApi.fixedAssetInsights().then((r) => r.items),
  });
  const register = useQuery({
    queryKey: ['finance', 'fixed-asset-register'],
    queryFn: () => financeApi.fixedAssetRegister().then((r) => r.items.assets),
  });

  const rows = register.data || [];
  const ins = insights.data || {};

  const runDepreciation = useMutation({
    mutationFn: () => financeApi.runDepreciation(period),
    onSuccess: (data) => {
      setRunMsg(t('fa.run.done', { count: String(data.postedCount), total: formatCurrency(data.totalAmount) }));
      queryClient.invalidateQueries({ queryKey: ['finance', 'fixed-asset-register'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'fixed-asset-insights'] });
    },
    onError: (err) => setRunMsg(err?.message || t('common.loadError')),
  });

  const columns = [
    { key: 'assetNumber', label: t('fa.col.number') },
    { key: 'name', label: t('fa.col.name') },
    { key: 'category', label: t('fa.col.category'), render: (r) => r.category || '-' },
    { key: 'acquisitionCost', label: t('fa.col.cost'), align: 'right', render: (r) => formatCurrency(r.acquisitionCost) },
    { key: 'accumulatedDepreciation', label: t('fa.col.accumulated'), align: 'right', render: (r) => formatCurrency(r.accumulatedDepreciation) },
    { key: 'netBookValue', label: t('fa.col.nbv'), align: 'right', render: (r) => <strong>{formatCurrency(r.netBookValue)}</strong> },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'neutral'}>{t(`fa.status.${r.status}`)}</Badge> },
    {
      key: 'actions',
      label: '',
      render: (r) => (r.status === 'DISPOSED'
        ? null
        : <Button size="sm" variant="ghost" onClick={() => setDisposing(r)}>{t('fa.action.dispose')}</Button>),
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('fa.title')}</h1>
          <p className="muted">{t('fa.subtitle')}</p>
        </div>
        <div className="page-actions">
          <CsvExportButton rows={rows} columns={CSV_COLUMNS} filename="anlagen" />
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('fa.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('fa.metric.active')} value={String(ins.activeCount ?? 0)} />
        <MetricCard label={t('fa.metric.cost')} value={formatCurrency(ins.totalCost ?? 0)} />
        <MetricCard label={t('fa.metric.accumulated')} value={formatCurrency(ins.totalAccumulatedDepreciation ?? 0)} />
        <MetricCard label={t('fa.metric.nbv')} value={formatCurrency(ins.totalNetBookValue ?? 0)} />
      </div>

      <div className="card" style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <label className="field" style={{ maxWidth: 220 }}>
          <span className="field-label">{t('fa.run.period')}</span>
          <input className="input" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
        </label>
        <Button variant="secondary" onClick={() => runDepreciation.mutate()} disabled={runDepreciation.isPending || !period}>
          {runDepreciation.isPending ? `${t('fa.run.action')}…` : t('fa.run.action')}
        </Button>
        {runMsg && <span className="muted" style={{ alignSelf: 'center' }}>{runMsg}</span>}
      </div>

      <Table columns={columns} rows={rows} isLoading={register.isLoading} emptyMessage={t('fa.empty')} />

      <AssetCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <DisposeAssetModal asset={disposing} onClose={() => setDisposing(null)} />
    </section>
  );
}
